import { youtubeEmbedUrl } from '../utils/youtube';
import styles from './VideoEmbed.module.css';

interface VideoEmbedProps {
  /** A YouTube address as an administrator pasted it. */
  url: string;
  /** What the video is called, to a screen reader and as the section's heading. */
  label: string;
}

/**
 * A YouTube video, sized to whatever it is given.
 *
 * Renders nothing at all -- heading and section included, which is why the
 * section is built here rather than around this component -- when there is no
 * video, or when the address is not one a video can be read out of. A memorial
 * with an empty grey box on it looks broken and invites the question of what is
 * missing, which is not a question to put to somebody who came here to remember
 * a person. Leaving the heading behind would be worse still: a screen reader
 * would announce a video that is not there.
 *
 * The address embedded is composed from the video identifier, never taken from
 * what was pasted -- see utils/youtube.ts, which is also where the autoplay,
 * mute and same-channel-suggestions parameters are set out and explained.
 */
export function VideoEmbed({ url, label }: VideoEmbedProps) {
  const src = youtubeEmbedUrl(url);
  if (src === '') return null;

  return (
    <section className={styles.section} aria-labelledby="video-heading">
      <h2 id="video-heading" className="visually-hidden">
        {label}
      </h2>
      <div className={styles.frame}>
        <iframe
          src={src}
          title={label}
          // Delegates autoplay to YouTube's origin. Without this the browser
          // refuses it to a cross-origin frame whatever the address asks for.
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          // Sends the origin but never the path, matching the site's own
          // Referrer-Policy: an invitation link in the address bar must not
          // travel to YouTube.
          referrerPolicy="strict-origin-when-cross-origin"
          // Nothing is asked of YouTube until the visitor scrolls near the
          // video. That keeps the video from being half over by the time anyone
          // reaches it, and means a visitor who only reads the top of the page
          // never contacts YouTube at all.
          loading="lazy"
        />
      </div>
      <p className={styles.caption}>The video starts without sound. Use the controls to turn it on.</p>
    </section>
  );
}
